/**
 * The scrubbed universe: summary, timeliness by scenario, the issues list
 * and the universe itself with every issue on its cell. Used by the Validate
 * screen and by compliance when reviewing a held submission.
 */
import React, { useMemo, useState } from 'react';
import { Card, Stat, Pill, Notice, Table, btn, input, label as labelCls } from './ui';
import { fmtNum, pct } from './api';

const CAT_TONE = {
  'Timeliness': 'amber', 'Data Accuracy': 'rose', 'Missing Data': 'violet', 'Missing Info': 'violet', 'Formatting Issues': 'sky', 'Bad Format': 'sky', 'Wrong Order': 'sky', 'Universe Period': 'slate',
};
const toneFor = (c) => CAT_TONE[c] || 'slate';
const issueText = (iss) => (typeof iss === 'string' ? iss : (iss && (iss.message || iss.title)) || '');
const rateTone = (r) => (r === null || r === undefined ? 'slate' : r >= 95 ? 'emerald' : r >= 90 ? 'amber' : 'rose');

// Rows that carry at least one issue. The server's totalFailedRows counts
// issues, not rows, so the rows on screen are counted directly when present.
export function rowsWithIssues(result) {
  const u = result?.universe || [];
  if (u.length) return u.filter((r) => (r.issues && r.issues.length) || r.timelinessCategory || Object.values(r.data || {}).some((c) => c && c.issue)).length;
  const rows = new Set((result?.errors || []).filter((e) => e.row !== 'Header').map((e) => e.row));
  return rows.size;
}

export function categoryCountsOf(result) {
  if (result?.categoryCounts && Object.keys(result.categoryCounts).length) return result.categoryCounts;
  const acc = {};
  for (const e of result?.errors || []) { const c = e.category || 'Other'; acc[c] = (acc[c] || 0) + 1; }
  return acc;
}

export function TimelinessPanel({ summary, monthly }) {
  const scenarios = summary?.scenarios || [];
  const overall = summary?.overall || null;
  if (!scenarios.length && !overall) return null;
  const overallText = { emerald: 'text-emerald-600', amber: 'text-amber-600', rose: 'text-rose-600', slate: 'text-slate-900' };
  return (
    <Card title="Timeliness by scenario" subtitle="Every case is judged against the CMS clock for its scenario; the overall rate is timely cases over applicable cases.">
      {overall && (
        <div className="flex items-center gap-6 mb-4 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Overall</div>
            <div className={`text-2xl font-bold tracking-tight ${overallText[rateTone(overall.rate)]}`}>{overall.rate === null || overall.rate === undefined ? 'n/a' : pct(overall.rate)}</div>
          </div>
          <div className="text-sm text-slate-600">{fmtNum(overall.timely)} timely · {fmtNum(overall.untimely)} untimely · {fmtNum(overall.applicable ?? (overall.timely + overall.untimely))} applicable</div>
        </div>
      )}
      {scenarios.length > 0 && (
        <Table flat head={['Scenario', { label: 'Timely', right: true }, { label: 'Untimely', right: true }, { label: 'Not applicable', right: true }, { label: 'Rate', right: true }]}>
          {scenarios.map((s) => (
            <tr key={s.scenario}>
              <td className="px-3 py-2 text-slate-800">{s.scenario}</td>
              <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtNum(s.timely)}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${s.untimely > 0 ? 'text-rose-600' : 'text-slate-500'}`}>{fmtNum(s.untimely)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtNum(s.notApplicable)}</td>
              <td className="px-3 py-2 text-right"><Pill tone={rateTone(s.rate)}>{s.rate === null || s.rate === undefined ? 'n/a' : pct(s.rate)}</Pill></td>
            </tr>
          ))}
        </Table>
      )}
      {Array.isArray(monthly) && monthly.length > 0 && (
        <div className="mt-4">
          <div className={labelCls}>By month</div>
          <Table flat head={['Month', 'Scenario', { label: 'Timely', right: true }, { label: 'Untimely', right: true }, { label: 'Rate', right: true }]}>
            {[...monthly].sort((a, b) => String(a.month).localeCompare(String(b.month)) || String(a.scenario).localeCompare(String(b.scenario))).map((m, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-xs text-slate-700">{m.month}</td><td className="px-3 py-1.5">{m.scenario}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(m.timely)}</td><td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(m.untimely)}</td>
                <td className="px-3 py-1.5 text-right"><Pill tone={rateTone(m.rate)}>{m.rate === null || m.rate === undefined ? 'n/a' : pct(m.rate)}</Pill></td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </Card>
  );
}

function IssuesTable({ errors, categories }) {
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const per = 50;
  const list = useMemo(() => (errors || []).filter((e) => (!cat || e.category === cat) && (!q || `${e.row} ${e.field} ${e.message}`.toLowerCase().includes(q.toLowerCase()))), [errors, cat, q]);
  const pages = Math.max(1, Math.ceil(list.length / per));
  const slice = list.slice(page * per, page * per + per);
  return (
    <Card title="Issues" subtitle={`${fmtNum(list.length)} of ${fmtNum((errors || []).length)} shown`} padding="p-0" shadow="shadow-md" right={(
      <>
        <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" value={cat} onChange={(e) => { setCat(e.target.value); setPage(0); }}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="search" className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white w-56" placeholder="Search row, field, message" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
      </>
    )}>
      {list.length === 0 ? <div className="py-12 text-center text-slate-500 text-sm">No issues match.</div> : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left"><th className="py-3 px-3 text-xs font-semibold text-slate-600">Row</th><th className="py-3 px-3 text-xs font-semibold text-slate-600">Field</th><th className="py-3 px-3 text-xs font-semibold text-slate-600">Issue</th><th className="py-3 px-3 text-xs font-semibold text-slate-600">Category</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {slice.map((e, i) => (
                  <tr key={i} className="align-top">
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap tabular-nums">{e.row}</td>
                    <td className="px-3 py-2 text-slate-800">{e.field}</td>
                    <td className="px-3 py-2 text-slate-700">{e.message}</td>
                    <td className="px-3 py-2"><Pill tone={toneFor(e.category)}>{e.category}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 border-t border-slate-100"><Pager page={page} pages={pages} setPage={setPage} /></div>
        </>
      )}
    </Card>
  );
}

function Pager({ page, pages, setPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 text-xs text-slate-600">
      <button type="button" className={btn.secondarySmall} disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
      <span>Page {page + 1} of {pages}</span>
      <button type="button" className={btn.secondarySmall} disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next</button>
    </div>
  );
}

function UniverseTable({ universe, totalRows, universeMode, cleanRowsOmitted }) {
  const [onlyErrored, setOnlyErrored] = useState(false);
  const [cats, setCats] = useState([]);
  const [page, setPage] = useState(0);
  const per = 50;
  const headers = useMemo(() => (universe && universe.length ? Object.keys(universe[0].data || {}) : []), [universe]);
  const counts = useMemo(() => {
    const acc = {};
    for (const row of universe || []) {
      Object.values(row.data || {}).forEach((cell) => { if (cell && cell.category) acc[cell.category] = (acc[cell.category] || 0) + 1; });
      (row.issues || []).forEach((iss) => { if (iss?.category && iss?.field && !(iss.field in (row.data || {}))) acc[iss.category] = (acc[iss.category] || 0) + 1; });
      if (row.timelinessCategory === 'Timeliness' && !Object.values(row.data || {}).some((c) => c && c.category === 'Timeliness')) acc.Timeliness = (acc.Timeliness || 0) + 1;
    }
    return acc;
  }, [universe]);
  const rowHas = (row, cat) => Object.values(row.data || {}).some((c) => c && c.category === cat) || (row.issues || []).some((i) => i.category === cat) || (cat === 'Timeliness' && row.timelinessCategory === 'Timeliness');
  const rowErrored = (row) => (row.issues && row.issues.length) || row.timelinessCategory || Object.values(row.data || {}).some((c) => c && c.issue);
  const list = useMemo(() => (universe || []).filter((r) => (!onlyErrored || rowErrored(r)) && (!cats.length || cats.some((c) => rowHas(r, c)))), [universe, onlyErrored, cats]);
  const pages = Math.max(1, Math.ceil(list.length / per));
  const slice = list.slice(page * per, page * per + per);
  if (!universe || !universe.length) return null;
  return (
    <Card title="Universe" subtitle={universeMode === 'issues-only'
      ? `A large file: the ${fmtNum(universe.length)} rows that need review are shown; ${fmtNum(cleanRowsOmitted)} clean rows are not sent to the browser. The export carries the complete universe.`
      : `${fmtNum(totalRows || universe.length)} rows. Cells with an issue are marked; hover a cell to read it.`}
      padding="p-0" shadow="shadow-md">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <label className="text-xs text-slate-700 flex items-center gap-1.5 mr-2"><input type="checkbox" className="accent-orange-600" checked={onlyErrored} onChange={(e) => { setOnlyErrored(e.target.checked); setPage(0); }} /> Only rows with issues</label>
        {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
          <button key={c} type="button" onClick={() => { setCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c])); setPage(0); }}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${cats.includes(c) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
            {c} <span className="tabular-nums">{fmtNum(n)}</span>
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-auto">{fmtNum(list.length)} rows shown</span>
      </div>
      <div className="overflow-auto max-h-[60vh]">
        <table className="text-xs border-collapse min-w-full">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-500 border-b border-slate-200 whitespace-nowrap">Row</th>
              {headers.map((h) => <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {slice.map((row) => (
              <tr key={row.rowIndex}>
                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap tabular-nums">
                  {row.rowIndex}
                  {row.timelinessCategory && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-amber-500" title="Timeliness issue on this row" />}
                </td>
                {headers.map((h) => {
                  const cell = (row.data || {})[h] || {};
                  const bad = !!cell.issue;
                  return (
                    <td key={h} title={bad ? `${cell.category || ''}: ${issueText(cell.issue)}` : undefined}
                      className={`px-3 py-1.5 whitespace-nowrap max-w-[22rem] truncate ${bad ? 'bg-rose-50 text-rose-800 shadow-[inset_2px_0_0_0_#fb7185]' : 'text-slate-700'}`}>
                      {String(cell.value ?? '')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-slate-100"><Pager page={page} pages={pages} setPage={setPage} /></div>
    </Card>
  );
}

export default function ResultsView({ result, title, meta, actions, headerCorrections, onHeaderCorrections }) {
  if (!result) return null;
  const counts = categoryCountsOf(result);
  const categories = Object.keys(counts).sort();
  const totalErrors = result.totalErrorCount ?? (result.errors || []).length;
  const totalRows = result.totalRows ?? result.stats?.totalRows ?? (result.universe || []).length;
  const headerIssues = (result.errors || []).filter((e) => e.row === 'Header');
  const timely = result.timelinessSummary?.overall;
  return (
    <div className="space-y-4">
      {(title || actions) && (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            {title && <h2 className="text-base font-semibold text-slate-900">{title}</h2>}
            {meta && <div className="text-xs text-slate-500 mt-0.5">{meta}</div>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap justify-end">{actions}</div>}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Rows" value={fmtNum(totalRows)} />
        <Stat label="Issues" value={fmtNum(totalErrors)} tone={totalErrors > 0 ? 'rose' : 'emerald'} hint={result.errorsTruncated ? `List capped at ${fmtNum((result.errors || []).length)}` : undefined} />
        <Stat label="Rows with issues" value={fmtNum(rowsWithIssues(result))} tone="amber" hint={result.universeMode === 'issues-only' ? 'Of the rows sent for review' : undefined} />
        <Stat label="Timely" value={timely && timely.rate !== null && timely.rate !== undefined ? pct(timely.rate) : 'n/a'} tone={timely ? (timely.rate >= 95 ? 'emerald' : timely.rate >= 90 ? 'amber' : 'rose') : 'slate'} hint={timely ? `${fmtNum(timely.timely)} of ${fmtNum(timely.applicable ?? (timely.timely + timely.untimely))}` : 'No timeliness scenarios for this table'} />
      </div>
      {headerIssues.length > 0 && (
        <HeaderFix issues={headerIssues} corrections={headerCorrections} onChange={onHeaderCorrections} />
      )}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([c, n]) => <Pill key={c} tone={toneFor(c)}>{c} · {fmtNum(n)}</Pill>)}
        </div>
      )}
      <TimelinessPanel summary={result.timelinessSummary} monthly={result.monthlyTimeliness} />
      {Array.isArray(result.crossRowWarnings) && result.crossRowWarnings.length > 0 && (
        <Notice tone="amber"><div className="font-semibold mb-1">Cross row checks</div><ul className="list-disc pl-5">{result.crossRowWarnings.slice(0, 20).map((w, i) => <li key={i}>{typeof w === 'string' ? w : w.message || JSON.stringify(w)}</li>)}</ul></Notice>
      )}
      <IssuesTable errors={result.errors || []} categories={categories} />
      <UniverseTable universe={result.universe} totalRows={totalRows} universeMode={result.universeMode} cleanRowsOmitted={result.universeCleanRowsOmitted} />
    </div>
  );
}

function HeaderFix({ issues, corrections, onChange }) {
  const missing = issues.filter((i) => i.category === 'Missing Info').map((i) => i.field);
  const extra = issues.filter((i) => i.category === 'Bad Format').map((i) => i.field);
  const canFix = !!onChange && missing.length > 0 && extra.length > 0;
  return (
    <Notice tone="amber">
      <div className="font-semibold">The column headers do not match the CMS layout.</div>
      <ul className="list-disc pl-5 mt-1 text-xs">{issues.map((i, k) => <li key={k}><span className="font-semibold">{i.field}</span>: {i.message}</li>)}</ul>
      {canFix && (
        <div className="mt-3 border-t border-amber-200 pt-3">
          <div className="text-xs mb-2">Map each unexpected column to the CMS column it stands for, then run the validation again.</div>
          <div className="grid md:grid-cols-2 gap-2">
            {extra.map((found) => (
              <div key={found} className="flex items-center gap-2 text-xs">
                <span className="truncate flex-1 font-semibold" title={found}>{found}</span>
                <span className="text-slate-400">to</span>
                <select className={`${input} w-auto flex-1`} value={(corrections || {})[found] || ''} onChange={(e) => onChange({ ...(corrections || {}), [found]: e.target.value || undefined })}>
                  <option value="">Leave as is</option>
                  {missing.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </Notice>
  );
}
